// ./src/components/features/Vehicle/VehicleTypeIcon/VehicleTypeIcon.tsx
import React from "react";
import { ThemeIcon, ThemeIconProps } from "@mantine/core";

import { VehicleType } from "@/utils/carHelpers";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface VehicleTypeIconProps {
  /** Vehicle type to display icon for */
  vehicleType?: VehicleType | null;

  /** Icon size in pixels */
  size?: number;

  /** Theme icon size (for the wrapper) */
  iconSize?: ThemeIconProps["size"];

  /** Icon color (defaults to theme color based on vehicle type) */
  color?: string;

  /** Theme icon variant */
  variant?: ThemeIconProps["variant"];

  /** Theme icon radius */
  radius?: ThemeIconProps["radius"];

  /** Whether to show just the icon without ThemeIcon wrapper */
  bare?: boolean;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const VEHICLE_TYPE_COLORS: Record<VehicleType, string> = {
  car: "brand",
  motorcycle: "orange",
  truck: "blue",
  other: "gray",
};

const VEHICLE_TYPE_ICONS: Record<VehicleType, string> = {
  car: "Car",
  motorcycle: "Motorbike",
  truck: "Truck",
  other: "QuestionMark",
};

// ============================================
// Component
// ============================================

/**
 * Displays an icon representing the vehicle type.
 * Can be rendered as a bare icon or wrapped in a ThemeIcon.
 */
export const VehicleTypeIcon: React.FC<VehicleTypeIconProps> = ({
  vehicleType = "car",
  size = 24,
  iconSize = "lg",
  color,
  variant = "light",
  radius = "md",
  bare = false,
  className,
}) => {
  const type = vehicleType || "car";
  const iconColor = color || VEHICLE_TYPE_COLORS[type];
  const iconName = VEHICLE_TYPE_ICONS[type];

  const icon = <AppIcon iconName={iconName} size={size} stroke={1.5} />;

  // Return bare icon if requested
  if (bare) {
    return icon;
  }

  // Return icon wrapped in ThemeIcon
  return (
    <ThemeIcon
      size={iconSize}
      variant={variant}
      radius={radius}
      color={iconColor}
      className={className}
    >
      {icon}
    </ThemeIcon>
  );
};
